<template>
  <AppLayout>
    <div class="groups-list-view">
      <div class="header">
        <div>
          <h1>My Groups</h1>
          <p class="subtitle">Manage and organize your meal planning groups</p>
        </div>
        <button @click="createGroup" class="btn btn-primary">
          <i class="icon-plus"></i>
          Create New Group
        </button>
      </div>

      <!-- Search and Filters -->
      <div class="controls">
        <div class="search-bar">
          <i class="icon-search"></i>
          <input v-model="searchQuery" type="text" placeholder="Search groups by name or description..."
            class="search-input" @input="debouncedSearch" />
        </div>

        <div class="filters">
          <button @click="togglePersonalFilter" class="filter-btn" :class="{ 'active': showPersonalGroups }">
            <i class="icon-lock"></i>
            Personal Groups
          </button>

          <select v-model="sortBy" class="sort-select" @change="handleSort">
            <option value="created_at">Sort by Date Created</option>
            <option value="name">Sort by Name</option>
            <option value="updated_at">Sort by Last Updated</option>
          </select>
        </div>
      </div>

      <!-- Loading State with Skeletons -->
      <div v-if="isLoading" class="loading-state">
        <div class="groups-grid">
          <SkeletonLoader v-for="n in 6" :key="n" type="card" />
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <i class="icon-error"></i>
        <p class="error">{{ error }}</p>
        <button @click="retry" class="btn btn-secondary">Try Again</button>
      </div>

      <!-- Empty State -->
      <div v-else-if="filteredGroups.length === 0" class="empty-state">
        <div v-if="searchQuery || !showPersonalGroups">
          <i class="icon-search-empty"></i>
          <h3>No groups found</h3>
          <p>Try adjusting your search or filters</p>
          <button @click="clearFilters" class="btn btn-secondary">
            Clear Filters
          </button>
        </div>
        <div v-else>
          <i class="icon-groups"></i>
          <h3>Welcome to Groups!</h3>
          <p>Create your first group to start organizing meals with others</p>
          <button @click="createGroup" class="btn btn-primary">
            Create Your First Group
          </button>
        </div>
      </div>

      <!-- Groups Grid -->
      <div v-else>
        <div class="groups-header">
          <p class="groups-count">
            Showing {{ filteredGroups.length }} of {{ totalItems }} groups
          </p>
        </div>

        <div class="groups-grid">
          <GroupCard v-for="group in paginatedGroups" :key="group.id" :group="group" :show-actions="true"
            :can-edit="canEditGroup(group)" :members="members" @click="viewGroup(group.id)" @view="viewGroup(group.id)"
            @edit="editGroup(group.id)" />
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="pagination">
          <button @click="goToPage(currentPage - 1)" :disabled="currentPage === 1" class="pagination-btn">
            Previous
          </button>

          <div class="pagination-numbers">
            <button v-for="page in visiblePages" :key="page" @click="goToPage(page)" class="pagination-number"
              :class="{ 'active': page === currentPage }">
              {{ page }}
            </button>
          </div>

          <button @click="goToPage(currentPage + 1)" :disabled="currentPage === totalPages" class="pagination-btn">
            Next
          </button>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useGroupsStore } from '../../stores/groups';
import { useAuthStore } from '../../stores/auth';
import { storeToRefs } from 'pinia';
import { debounce } from 'lodash-es';
import { useKeyboardShortcuts } from '../../composables/useKeyboardShortcuts';
import AppLayout from '../../components/layout/AppLayout.vue';
import GroupCard from '../../components/groups/GroupCard.vue';
import SkeletonLoader from '../../components/ui/SkeletonLoader.vue';
import type { Group } from '../../types/group';

const router = useRouter();
const groupsStore = useGroupsStore();
const authStore = useAuthStore();
const { initializeGroupsShortcuts } = useKeyboardShortcuts();

const {
  filteredGroups,
  members,
  isLoading,
  error,
  currentPage,
  totalPages,
  totalItems,
  searchQuery,
  showPersonalGroups,
  sortBy,
  pageSize
} = storeToRefs(groupsStore);

const paginatedGroups = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value;
  const end = start + pageSize.value;
  return filteredGroups.value.slice(start, end);
});

const visiblePages = computed(() => {
  const pages: number[] = [];
  const maxVisible = 5;
  const halfVisible = Math.floor(maxVisible / 2);

  let start = Math.max(1, currentPage.value - halfVisible);
  let end = Math.min(totalPages.value, currentPage.value + halfVisible);

  if (end - start < maxVisible - 1) {
    if (start === 1) {
      end = Math.min(totalPages.value, maxVisible);
    } else {
      start = Math.max(1, totalPages.value - maxVisible + 1);
    }
  }

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  return pages;
});

onMounted(async () => {
  await groupsStore.fetchGroups();
  initializeGroupsShortcuts();
});

const debouncedSearch = debounce(async () => {
  currentPage.value = 1;
  await groupsStore.fetchGroups();
}, 300);

const togglePersonalFilter = () => {
  showPersonalGroups.value = !showPersonalGroups.value;
  currentPage.value = 1;
  groupsStore.fetchGroups();
};

const handleSort = () => {
  currentPage.value = 1;
  groupsStore.fetchGroups();
};

const clearFilters = () => {
  groupsStore.resetFilters();
  groupsStore.fetchGroups();
};

const goToPage = (page: number) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
    groupsStore.fetchGroups();
  }
};

const canEditGroup = (group: Group) => {
  return group.owner_id === authStore.currentUser?.id ||
    group.members?.some(m =>
      m.user_id === authStore.currentUser?.id &&
      m.role === 'admin'
    );
};

const createGroup = () => {
  router.push('/groups/new');
};

const viewGroup = (id: number) => {
  router.push(`/groups/${id}`);
};

const editGroup = (id: number) => {
  router.push(`/groups/${id}/edit`);
};

const retry = async () => {
  await groupsStore.fetchGroups();
};
</script>

<style scoped>
@import '../../assets/main.css';

.groups-list-view {
  padding: var(--spacing-8);
  max-width: 1400px;
  margin: 0 auto;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-8);
  padding-bottom: var(--spacing-6);
  border-bottom: 1px solid rgb(229 231 235);
}

.header h1 {
  margin: 0;
  color: var(--color-neutral-dark-gray);
  font-size: var(--font-size-3xl);
  font-weight: var(--font-weight-bold);
}

.subtitle {
  margin: var(--spacing-2) 0 0 0;
  color: rgb(107 114 128);
  font-size: var(--font-size-base);
}

/* Controls Section */
.controls {
  display: flex;
  gap: var(--spacing-6);
  margin-bottom: var(--spacing-8);
  flex-wrap: wrap;
  align-items: center;
}

.search-bar {
  position: relative;
  flex: 1;
  min-width: 300px;
}

.search-input {
  width: 100%;
  padding: var(--spacing-3) var(--spacing-4) var(--spacing-3) 2.75rem;
  border: 1px solid rgb(229 231 235);
  border-radius: var(--radius-lg);
  font-size: var(--font-size-base);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.search-input:focus {
  outline: 2px solid var(--color-primary-500);
  border-color: transparent;
  box-shadow: 0 0 0 4px rgba(76, 175, 80, 0.1);
}

.icon-search {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
}

.filters {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.filter-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-2);
  padding: var(--spacing-2) var(--spacing-4);
  border: 1px solid rgb(229 231 235);
  border-radius: var(--radius-lg);
  background: white;
  color: rgb(75 85 99);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.filter-btn:hover {
  background-color: rgb(249 250 251);
  border-color: var(--color-primary-300);
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.filter-btn.active {
  background-color: var(--color-primary-500);
  color: white;
  border-color: var(--color-primary-500);
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
}

.sort-select {
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.sort-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Loading, Error, and Empty States */
.loading-state,
.error-state,
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.spinner-large {
  width: var(--spacing-12);
  height: var(--spacing-12);
  margin: 0 auto var(--spacing-4);
  border: 3px solid rgb(229 231 235);
  border-top-color: var(--color-primary-500);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error {
  color: #ef4444;
  margin: 1rem 0;
  font-size: 1.125rem;
}

.empty-state h3 {
  margin: 1rem 0 0.5rem 0;
  color: #1f2937;
  font-size: 1.5rem;
}

.empty-state p {
  color: #6b7280;
  margin-bottom: 1.5rem;
}

/* Groups Section */
.groups-header {
  margin-bottom: 1.5rem;
}

.groups-count {
  color: #6b7280;
  font-size: 0.875rem;
}

.groups-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #e5e7eb;
}

.pagination-btn,
.pagination-number {
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  color: #4b5563;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled),
.pagination-number:hover {
  border-color: #3b82f6;
  background: #eff6ff;
  color: #2563eb;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-number.active {
  background: #3b82f6;
  border-color: #3b82f6;
  color: white;
}

.pagination-numbers {
  display: flex;
  gap: 0.5rem;
}


/* Icons */
.icon-plus::before {
  content: '➕';
}

.icon-search::before {
  content: '🔍';
}

.icon-lock::before {
  content: '🔒';
}

.icon-error::before {
  content: '❌';
  font-size: 2rem;
  display: block;
  margin-bottom: 1rem;
}

.icon-search-empty::before {
  content: '🔎';
  font-size: 3rem;
  display: block;
  margin-bottom: 1rem;
}

.icon-groups::before {
  content: '👥';
  font-size: 3rem;
  display: block;
  margin-bottom: 1rem;
}
</style>
